import type {
  Kpi,
  MetricType,
} from 'lumen/models/market-performance/data-item';

import {
  GROUP_COLUMNS_BY,
  OVERVIEW_COLUMNS,
  type OverviewColumn,
  type TableLayoutPreferences,
} from './constants/market-snapshots-constants';

export interface ColumnSettings<T extends string = string> {
  hidden: T[];
  order: T[];
}

export function defaultColumnSettings<T extends string>(
  ids: readonly T[],
): ColumnSettings<T> {
  return { order: [...ids], hidden: [] };
}

export function getVisibleKpis({ hidden, order }: ColumnSettings<Kpi>): Kpi[] {
  return order.filter((kpi) => !hidden.includes(kpi));
}

export function getVisibleMetricTypes(
  { hidden, order }: ColumnSettings<OverviewColumn>,
  { groupColumnsBy }: TableLayoutPreferences,
  metrics: Record<OverviewColumn, MetricType>,
): MetricType[] {
  // KPIs mode ignores category settings: OTB + every category, default order.
  if (groupColumnsBy === GROUP_COLUMNS_BY.KPIS) {
    return ['otb', ...OVERVIEW_COLUMNS.map((column) => metrics[column])];
  }

  return [
    'otb',
    ...order
      .filter((column) => !hidden.includes(column))
      .map((column) => metrics[column]),
  ];
}

/**
 * Validates stored column settings against the currently available `ids`:
 * unknown and duplicate ids are dropped, missing ids are appended to the order.
 * With `requireVisible`, hiding every id is treated as hiding none.
 */
export function parseColumnSettings<T extends string>(
  json: unknown,
  ids: readonly T[],
  requireVisible = false,
): ColumnSettings<T> {
  if (typeof json !== 'object' || json === null) {
    return defaultColumnSettings(ids);
  }

  const raw = json as Record<string, unknown>;
  const known = (value: unknown): T[] =>
    Array.isArray(value)
      ? [
          ...new Set(
            (value as unknown[]).filter((id): id is T => ids.includes(id as T)),
          ),
        ]
      : [];

  const order = known(raw.order);
  const hidden = known(raw.hidden);

  return {
    order: [...order, ...ids.filter((id) => !order.includes(id))],
    hidden: requireVisible && hidden.length === ids.length ? [] : hidden,
  };
}
